import { ReactNode } from 'react';
import styled from 'styled-components';

import { useAgentState } from '../../providers/contexts';
import { usePersonalization } from '../../providers/PersonalizationProvider';
import { GhostButton, Mono, Notice, PaneTitle, RowHelp } from './primitives';

/**
 * The heading, intro and availability states both personalization surfaces
 * share. `unavailable` replaces the body; a read error is a banner above it.
 */
export function PersonalizationFrame({
  children,
}: {
  readonly children: ReactNode;
}) {
  const api = usePersonalization();
  // A retry needs the socket; with it down the button would do nothing that
  // the user could see, which is indistinguishable from a broken one.
  const connected = useAgentState().status === 'authenticated';
  return (
    <>
      <PaneTitle>Personalization</PaneTitle>
      {api.status === 'unavailable' ? (
        <Notice>
          Personalization isn&apos;t available on this gateway — the
          agent&apos;s config files can&apos;t be read from here.
        </Notice>
      ) : (
        <Surface data-personalization-surface>
          <IntroText>
            Edit your agent&apos;s Persona (<Mono>SOUL.md</Mono>), Identity (
            <Mono>IDENTITY.md</Mono>) and what it knows about you (
            <Mono>USER.md</Mono>).
          </IntroText>
          {api.error != null && (
            <ErrorNotice role="alert">
              <span>{api.error}</span>
              <RetryButton
                type="button"
                disabled={!connected}
                onClick={() => void api.reload()}
              >
                {connected ? 'Retry' : 'Reconnecting…'}
              </RetryButton>
            </ErrorNotice>
          )}
          {children}
        </Surface>
      )}
    </>
  );
}

/** The element the editors' key layers own, so the Retry button is inside it. */
const Surface = styled.div`
  display: contents;
`;

const IntroText = styled(RowHelp)`
  max-width: none;
  margin: 6px 0 18px;
  line-height: 1.55;
`;

const ErrorNotice = styled(Notice)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 16px;
  border-color: var(--error-color);
  background: rgba(var(--error-color-rgb, 220, 80, 80), 0.08);
`;

const RetryButton = styled(GhostButton)`
  flex-shrink: 0;
`;
